const sectionSelector = '[data-cpsbrg-section]';
const galleryRequests = new Map();

function createGalleryButton(className, dataAttribute, dataValue, label) {
  const button = document.createElement('button');
  button.className = className;
  button.type = 'button';
  button.setAttribute(dataAttribute, dataValue);
  button.setAttribute('aria-label', label);
  return button;
}

async function ensureGalleryLoaded(panel) {
  if (!(panel instanceof HTMLElement) || panel.dataset.cpsbrgGalleryLoaded === 'true') return;

  const handle = panel.dataset.cpsbrgProductHandle;
  const stage = panel.querySelector('.custom-page-shop-by-room-grid__quick-gallery-stage');
  const gallery = panel.querySelector('[data-cpsbrg-gallery]');

  if (!handle || !(stage instanceof HTMLElement) || !(gallery instanceof HTMLElement)) {
    panel.dataset.cpsbrgGalleryLoaded = 'true';
    return;
  }

  try {
    if (!galleryRequests.has(handle)) {
      const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`;
      galleryRequests.set(
        handle,
        fetch(productUrl, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
      );
    }

    const product = await galleryRequests.get(handle);
    const productMedia = product?.media || product?.images || [];
    const media = productMedia
      .filter((item) => typeof item === 'string' || item.media_type === 'image')
      .map((item, index) => ({
        id: typeof item === 'string' ? index : item.id,
        alt: typeof item === 'string' ? '' : item.alt,
        source: typeof item === 'string' ? item : item.preview_image?.src || item.src,
      }))
      .filter((item) => item.source);

    if (!media.length) {
      panel.dataset.cpsbrgGalleryLoaded = 'true';
      return;
    }

    stage.querySelectorAll('[data-cpsbrg-gallery-slide]').forEach((slide) => slide.remove());

    const fragment = document.createDocumentFragment();

    media.forEach((item, mediaIndex) => {
      const figure = document.createElement('figure');
      figure.className = 'custom-page-shop-by-room-grid__quick-slide';
      figure.dataset.cpsbrgGallerySlide = '';
      figure.dataset.cpsbrgGalleryIndex = String(mediaIndex);
      figure.dataset.cpsbrgMediaId = String(item.id || '');
      figure.dataset.cpsbrgMediaUrl = item.source;
      figure.hidden = true;

      const image = document.createElement('img');
      image.src = item.source;
      image.alt = item.alt || product.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      figure.append(image);
      fragment.append(figure);
    });

    stage.prepend(fragment);

    const slides = Array.from(stage.querySelectorAll('[data-cpsbrg-gallery-slide]'));
    slides.forEach((slide, index) => {
      slide.dataset.cpsbrgGalleryIndex = String(index);
      slide.hidden = index !== 0;
    });

    if (slides.length > 1 && !gallery.querySelector('[data-cpsbrg-gallery-thumb]')) {
      const previous = createGalleryButton(
        'custom-page-shop-by-room-grid__quick-gallery-button custom-page-shop-by-room-grid__quick-gallery-button--prev',
        'data-cpsbrg-gallery-nav',
        'previous',
        'Show previous product image'
      );
      previous.textContent = '‹';
      const next = createGalleryButton(
        'custom-page-shop-by-room-grid__quick-gallery-button custom-page-shop-by-room-grid__quick-gallery-button--next',
        'data-cpsbrg-gallery-nav',
        'next',
        'Show next product image'
      );
      next.textContent = '›';
      stage.append(previous, next);

      const count = document.createElement('p');
      count.className = 'custom-page-shop-by-room-grid__quick-gallery-count';
      count.setAttribute('aria-live', 'polite');
      count.innerHTML = `<span data-cpsbrg-gallery-current>1</span> / <span data-cpsbrg-gallery-total>${slides.length}</span>`;
      stage.append(count);

      const thumbs = document.createElement('div');
      thumbs.className = 'custom-page-shop-by-room-grid__quick-thumbs';
      thumbs.setAttribute('aria-label', 'Product gallery thumbnails');

      slides.forEach((slide, index) => {
        const source = slide.querySelector('img');
        if (!(source instanceof HTMLImageElement)) return;

        const thumb = createGalleryButton(
          `custom-page-shop-by-room-grid__quick-thumb${index === 0 ? ' custom-page-shop-by-room-grid__quick-thumb--active' : ''}`,
          'data-cpsbrg-gallery-index',
          String(index),
          `Preview image ${index + 1}`
        );
        thumb.dataset.cpsbrgGalleryThumb = '';
        thumb.dataset.cpsbrgMediaId = slide.dataset.cpsbrgMediaId || '';
        thumb.setAttribute('aria-current', index === 0 ? 'true' : 'false');

        const image = source.cloneNode();
        image.loading = 'lazy';
        thumb.append(image);
        thumbs.append(thumb);
      });

      gallery.append(thumbs);
    }

    delete panel.cpsbrgGalleryState;
    panel.dataset.cpsbrgGalleryLoaded = 'true';
  } catch (error) {
    panel.dataset.cpsbrgGalleryLoaded = 'error';
    console.warn('Unable to load the quick view gallery.', error);
  }
}

function getPanels(section) {
  return Array.from(section.querySelectorAll('[data-cpsbrg-panel]'));
}

function normalizeValue(value) {
  return String(value || '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/[–—]/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9"+]+/g, ' ')
    .trim();
}

function valueMatches(panelValue, requestValue) {
  const normalizedPanelValue = normalizeValue(panelValue);
  const normalizedRequestValue = normalizeValue(requestValue);

  return (
    normalizedPanelValue &&
    normalizedRequestValue &&
    (normalizedPanelValue === normalizedRequestValue ||
      normalizedPanelValue.includes(normalizedRequestValue) ||
      normalizedRequestValue.includes(normalizedPanelValue))
  );
}

function getVariantFinishValue(variant) {
  return variant?.finish || (variant?.options || []).find((value) => /black|white/i.test(String(value || ''))) || '';
}

function findPanel(section, request) {
  const panels = getPanels(section);
  const panelId = typeof request === 'string' ? request : request?.panelId;

  if (panelId) {
    const panel = panels.find((item) => item.getAttribute('data-cpsbrg-panel') === panelId);
    if (panel) return panel;
  }

  if (!request || typeof request === 'string') return null;

  const productHandle = normalizeValue(request.productHandle);
  const finish = request.finish;
  const size = request.size;
  const title = request.title;

  return (
    panels.find((panel) => {
      const panelHandle = normalizeValue(panel.dataset.cpsbrgProductHandle);
      const handleMatches = productHandle && panelHandle === productHandle;
      const finishMatches = !finish || valueMatches(panel.dataset.cpsbrgFinish, finish);
      const sizeMatches = !size || valueMatches(panel.dataset.cpsbrgSize, size);

      return handleMatches && finishMatches && sizeMatches;
    }) ||
    panels.find((panel) => {
      const panelHandle = normalizeValue(panel.dataset.cpsbrgProductHandle);
      return productHandle && panelHandle === productHandle;
    }) ||
    panels.find(
      (panel) =>
        valueMatches(panel.dataset.cpsbrgTitle, title) &&
        (!finish || valueMatches(panel.dataset.cpsbrgFinish, finish)) &&
        (!size || valueMatches(panel.dataset.cpsbrgSize, size))
    ) ||
    null
  );
}

function getVariants(panel) {
  const script = panel.querySelector('[data-cpsbrg-variants]');

  if (!script) return [];

  try {
    return JSON.parse(script.textContent || '[]');
  } catch (error) {
    console.warn('Unable to parse shop-by-room quick view variants.', error);
    return [];
  }
}

function getSelectedOptionValues(panel) {
  const values = [];

  panel.querySelectorAll('[data-cpsbrg-product-option]').forEach((group) => {
    const index = Number(group.getAttribute('data-option-index'));
    const active = group.querySelector('[data-cpsbrg-option][aria-pressed="true"]');

    if (!Number.isNaN(index) && active instanceof HTMLElement) {
      values[index] = active.dataset.optionValue || '';
    }
  });

  return values;
}

function setButtonText(button, text) {
  const label = button.querySelector('.add-to-cart-text__content span span');

  if (label) {
    label.textContent = text;
  }
}

function getSalePercent(variant) {
  const priceAmount = Number(variant?.priceAmount);
  const compareAtPriceAmount = Number(variant?.compareAtPriceAmount);

  if (
    Number.isFinite(priceAmount) &&
    Number.isFinite(compareAtPriceAmount) &&
    compareAtPriceAmount > priceAmount &&
    compareAtPriceAmount > 0
  ) {
    return Math.round(((compareAtPriceAmount - priceAmount) * 100) / compareAtPriceAmount);
  }

  const salePercent = Number(variant?.salePercent);
  return Number.isFinite(salePercent) && salePercent > 0 ? Math.round(salePercent) : 0;
}

function updatePrice(panel, variant) {
  const priceWrap = panel.querySelector('[data-cpsbrg-price-wrap]');
  if (priceWrap?.dataset.priceLocked === 'true') return;

  const price = panel.querySelector('[data-cpsbrg-price]');
  const compareGroup = panel.querySelector('[data-cpsbrg-compare-price-group]');
  const comparePrice = panel.querySelector('[data-cpsbrg-compare-price]');
  const saleBadge = panel.querySelector('[data-cpsbrg-sale-badge]');

  if (!priceWrap || !price || !variant?.price) return;

  const salePercent = getSalePercent(variant);
  const hasSale = Boolean(variant.compareAtPrice && salePercent > 0);

  priceWrap.hidden = false;
  priceWrap.classList.toggle('custom-page-shop-by-room-grid__quick-price--regular', !hasSale);
  price.textContent = variant.price;
  price.classList.toggle('price-item--sale', hasSale);

  if (compareGroup) {
    compareGroup.hidden = !hasSale;
  }

  if (comparePrice) {
    comparePrice.textContent = hasSale ? variant.compareAtPrice : '';
  }

  if (saleBadge) {
    saleBadge.hidden = !hasSale;
    saleBadge.textContent = hasSale ? `-${salePercent}% Sale` : '';
  }
}

function getGallerySlides(panel) {
  return Array.from(panel.querySelectorAll('[data-cpsbrg-gallery-slide]'));
}

function getGalleryThumbs(panel) {
  return Array.from(panel.querySelectorAll('[data-cpsbrg-gallery-thumb]'));
}

function getGalleryState(panel) {
  if (!(panel instanceof HTMLElement)) {
    return {
      allIndexes: [],
      visibleIndexes: [],
      groupData: { selectedFinish: '', starts: [] },
      groups: [],
      mediaIndexById: new Map(),
    };
  }

  if (panel.cpsbrgGalleryState) return panel.cpsbrgGalleryState;

  const slides = getGallerySlides(panel);
  const allIndexes = slides.map((_, index) => index);
  const variants = getVariants(panel);
  let groupData = { selectedFinish: '', starts: [] };

  try {
    groupData = JSON.parse(panel.querySelector('[data-cpsbrg-gallery-groups]')?.textContent || '{}');
  } catch (error) {
    groupData = { selectedFinish: '', starts: [] };
  }

  const mediaIndexById = new Map(
    slides.map((slide, index) => [String(slide.dataset.cpsbrgMediaId || ''), index])
  );

  const startsByKey = new Map();

  (groupData.starts || []).forEach((start) => {
    const key = normalizeValue(start.name);
    const index = mediaIndexById.get(String(start.mediaId || ''));

    if (key && Number.isInteger(index)) {
      startsByKey.set(key, {
        name: start.name,
        key,
        index,
      });
    }
  });

  [...(groupData.variants || []), ...variants].forEach((variant) => {
    const finish = getVariantFinishValue(variant);
    const key = normalizeValue(finish);
    const index = mediaIndexById.get(String(variant.mediaId || ''));

    if (key && Number.isInteger(index) && !startsByKey.has(key)) {
      startsByKey.set(key, {
        name: finish,
        key,
        index,
      });
    }
  });

  const starts = Array.from(startsByKey.values())
    .map((start) => ({
      name: start.name,
      key: start.key,
      index: start.index,
    }))
    .filter((start) => start.key && Number.isInteger(start.index))
    .sort((a, b) => a.index - b.index);

  const groups = starts.map((start, index) => {
    const nextStart = starts[index + 1];
    return {
      name: start.name,
      key: start.key,
      indexes: allIndexes.filter(
        (slideIndex) => slideIndex >= start.index && (!nextStart || slideIndex < nextStart.index)
      ),
    };
  });

  panel.cpsbrgGalleryState = {
    allIndexes,
    visibleIndexes: allIndexes,
    groupData,
    groups,
    mediaIndexById,
  };

  return panel.cpsbrgGalleryState;
}

function getGroupIndexes(panel, finishName) {
  const state = getGalleryState(panel);
  const key = normalizeValue(finishName);
  const group = state.groups.find((item) => item.key === key);

  return group?.indexes?.length ? group.indexes : state.allIndexes;
}

function setActiveSwatch(panel, finishName) {
  const key = normalizeValue(finishName);

  panel.querySelectorAll('[data-cpsbrg-option-group]').forEach((group) => {
    const optionButtons = Array.from(group.querySelectorAll('[data-cpsbrg-option]'));
    const hasMatchingValue = optionButtons.some((option) => normalizeValue(option.dataset.optionValue) === key);

    if (!hasMatchingValue) return;

    optionButtons.forEach((option) => {
      const isActive = normalizeValue(option.dataset.optionValue) === key;
      option.classList.toggle('custom-page-shop-by-room-grid__choice--active', isActive);
      option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  });
}

function updateThumbs(panel, visibleIndexes, activeIndex) {
  const visibleSet = new Set(visibleIndexes);

  getGalleryThumbs(panel).forEach((thumb) => {
    const thumbIndex = Number(thumb.dataset.cpsbrgGalleryIndex || -1);
    const isVisible = visibleSet.has(thumbIndex);
    const isActive = thumbIndex === activeIndex && isVisible;

    thumb.hidden = !isVisible;
    thumb.classList.toggle('custom-page-shop-by-room-grid__quick-thumb--active', isActive);
    thumb.setAttribute('aria-current', isActive ? 'true' : 'false');

    if (isActive) {
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  });
}

function getActiveGalleryIndex(panel) {
  const state = getGalleryState(panel);
  const slides = getGallerySlides(panel);
  const activeIndex = slides.findIndex((slide) => !slide.hidden);

  if (activeIndex >= 0 && state.visibleIndexes.includes(activeIndex)) {
    return activeIndex;
  }

  return state.visibleIndexes[0] ?? 0;
}

function updateGalleryCounter(panel, index) {
  const current = panel.querySelector('[data-cpsbrg-gallery-current]');
  const total = panel.querySelector('[data-cpsbrg-gallery-total]');
  const state = getGalleryState(panel);
  const visibleIndexes = state.visibleIndexes.length ? state.visibleIndexes : state.allIndexes;
  const visiblePosition = visibleIndexes.indexOf(index);

  if (current) {
    current.textContent = String(visiblePosition >= 0 ? visiblePosition + 1 : 1);
  }

  if (total) {
    total.textContent = String(visibleIndexes.length || state.allIndexes.length || 1);
  }
}

function showGallerySlide(panel, requestedIndex = 0) {
  const slides = getGallerySlides(panel);
  const state = getGalleryState(panel);

  if (!slides.length) return;

  const visibleIndexes = state.visibleIndexes.length ? state.visibleIndexes : state.allIndexes;
  const currentPosition = visibleIndexes.indexOf(Number(requestedIndex));
  const index =
    currentPosition >= 0
      ? visibleIndexes[currentPosition]
      : visibleIndexes[((Number(requestedIndex) % visibleIndexes.length) + visibleIndexes.length) % visibleIndexes.length];

  slides.forEach((slide, slideIndex) => {
    slide.hidden = !visibleIndexes.includes(slideIndex) || slideIndex !== index;
  });

  panel.dataset.cpsbrgGalleryIndex = String(index);
  updateGalleryCounter(panel, index);
  updateThumbs(panel, visibleIndexes, index);
}

function findGalleryIndexForVariant(panel, variant) {
  const slides = getGallerySlides(panel);

  if (!slides.length || !variant) return -1;

  if (variant.mediaId) {
    const mediaId = String(variant.mediaId);
    const mediaIndex = slides.findIndex((slide) => slide.dataset.cpsbrgMediaId === mediaId);

    if (mediaIndex >= 0) return mediaIndex;
  }

  if (variant.media) {
    const mediaUrl = String(variant.media);
    const mediaIndex = slides.findIndex((slide) => slide.dataset.cpsbrgMediaUrl === mediaUrl);

    if (mediaIndex >= 0) return mediaIndex;
  }

  return -1;
}

function applyGalleryGroup(panel, finishName, preferredIndex = getActiveGalleryIndex(panel)) {
  const state = getGalleryState(panel);
  state.visibleIndexes = getGroupIndexes(panel, finishName);
  setActiveSwatch(panel, finishName);
  const nextIndex = state.visibleIndexes.includes(preferredIndex)
    ? preferredIndex
    : (state.visibleIndexes[0] ?? state.allIndexes[0] ?? 0);

  showGallerySlide(panel, nextIndex);
  return state.visibleIndexes.length !== state.allIndexes.length;
}

function stepGallery(button) {
  const panel = button.closest('[data-cpsbrg-panel]');

  if (!(panel instanceof HTMLElement)) return;

  const state = getGalleryState(panel);
  const visibleIndexes = state.visibleIndexes.length ? state.visibleIndexes : state.allIndexes;
  const direction = button.dataset.cpsbrgGalleryNav === 'previous' ? -1 : 1;
  const activeIndex = getActiveGalleryIndex(panel);
  const currentPosition = visibleIndexes.indexOf(activeIndex);
  const nextPosition = currentPosition === -1
    ? 0
    : (currentPosition + direction + visibleIndexes.length) % visibleIndexes.length;

  showGallerySlide(panel, visibleIndexes[nextPosition] ?? 0);
}

function openGalleryZoom(button) {
  const panel = button.closest('[data-cpsbrg-panel]');

  if (!(panel instanceof HTMLElement)) return;

  const slides = getGallerySlides(panel);
  const activeSlide = slides.find((slide) => !slide.hidden);
  const image = activeSlide?.querySelector('img');
  const url = activeSlide?.dataset.cpsbrgMediaUrl || image?.currentSrc || image?.src;

  if (!url) return;

  window.open(url, '_blank', 'noopener');
}

function updateVariant(panel) {
  const variants = getVariants(panel);

  if (!variants.length) return;

  const selectedValues = getSelectedOptionValues(panel);
  const variant = variants.find((item) =>
    selectedValues.every((value, index) => !value || item.options?.[index] === value)
  );

  if (!variant) return;

  const variantInput = panel.querySelector('[data-cpsbrg-variant-id]');
  const addButton = panel.querySelector('[ref="addToCartButton"]');
  const addComponent = panel.querySelector('add-to-cart-component');
  const addText = panel.dataset.cpsbrgAddText || 'Add to Cart';
  const soldOutText = panel.dataset.cpsbrgSoldOutText || 'Sold out';

  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = String(variant.id);
    variantInput.disabled = !variant.available;
  }

  if (addButton instanceof HTMLButtonElement) {
    addButton.disabled = !variant.available;
    addButton.toggleAttribute('aria-disabled', !variant.available);
    setButtonText(addButton, variant.available ? addText : soldOutText);
  }

  if (addComponent instanceof HTMLElement && variant.media) {
    addComponent.dataset.productVariantMedia = variant.media;
  }

  updatePrice(panel, variant);

  const finishValue = variant.options?.find((value) => /black|white/i.test(String(value || '')));
  if (finishValue) {
    applyGalleryGroup(panel, finishValue, findGalleryIndexForVariant(panel, variant));
    return;
  }

  const variantGalleryIndex = findGalleryIndexForVariant(panel, variant);
  if (variantGalleryIndex >= 0) {
    showGallerySlide(panel, variantGalleryIndex);
  }
}

async function syncPanelPrice(panel, finish = '') {
  const priceSync = window.FancyMayProductPriceSync;

  if (!(panel instanceof HTMLElement) || typeof priceSync?.sync !== 'function') return;

  const variant = await priceSync.sync(panel, { finish });
  const finishValue = getVariantFinishValue(variant);

  if (finishValue) {
    applyGalleryGroup(panel, finishValue, findGalleryIndexForVariant(panel, variant));
  }
}

function selectOption(button) {
  const panel = button.closest('[data-cpsbrg-panel]');
  const group = button.closest('[data-cpsbrg-option-group]');

  if (!panel || !group) return;

  group.querySelectorAll('[data-cpsbrg-option]').forEach((option) => {
    const isActive = option === button;
    option.classList.toggle('custom-page-shop-by-room-grid__choice--active', isActive);
    option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  updateVariant(panel);
}

async function openPanel(section, panelRequest) {
  const dialog = section.querySelector('[data-cpsbrg-dialog]');
  const panel = findPanel(section, panelRequest);

  if (!(dialog instanceof HTMLDialogElement) || !panel) return;

  await ensureGalleryLoaded(panel);

  getPanels(section).forEach((item) => {
    item.hidden = item !== panel;
  });

  const title = panel.querySelector('h2');
  if (title?.id) {
    dialog.setAttribute('aria-labelledby', title.id);
  }

  section.cpsbrgLastFocus = document.activeElement;

  if (!dialog.open) {
    dialog.showModal();
  }

  const initialFinish =
    panelRequest && typeof panelRequest === 'object'
      ? panelRequest.finish || panelRequest.color
      : '';

  if (initialFinish) {
    applyGalleryGroup(panel, initialFinish);
  } else {
    const state = getGalleryState(panel);
    if (state.groupData?.selectedFinish) {
      applyGalleryGroup(panel, state.groupData.selectedFinish);
    }
  }

  showGallerySlide(panel, getActiveGalleryIndex(panel));
  updateVariant(panel);
  syncPanelPrice(panel, initialFinish);

  requestAnimationFrame(() => {
    dialog.focus({ preventScroll: true });
  });
}

function closeDialog(section) {
  const dialog = section.querySelector('[data-cpsbrg-dialog]');

  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  dialog.close();
}

function restoreFocus(section) {
  const lastFocus = section.cpsbrgLastFocus;

  if (lastFocus instanceof HTMLElement && document.contains(lastFocus)) {
    lastFocus.focus();
  }

  section.cpsbrgLastFocus = null;
}

function initRoomCardInteractions(section) {
  if (!section.matches('.custom-page-shop-by-room-grid')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  section.addEventListener('pointermove', (event) => {
    if (reducedMotion.matches) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const media = target.closest('.custom-page-shop-by-room-grid__media');
    if (!(media instanceof HTMLElement) || !section.contains(media)) return;

    const bounds = media.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;

    media.style.setProperty('--cpsbrg-pointer-x', `${Math.round(x)}%`);
    media.style.setProperty('--cpsbrg-pointer-y', `${Math.round(y)}%`);
  });

  section.addEventListener('pointerout', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const media = target.closest('.custom-page-shop-by-room-grid__media');
    const nextTarget = event.relatedTarget;
    if (!(media instanceof HTMLElement) || (nextTarget instanceof Node && media.contains(nextTarget))) return;

    media.style.removeProperty('--cpsbrg-pointer-x');
    media.style.removeProperty('--cpsbrg-pointer-y');
  });
}

function initSection(section) {
  if (!(section instanceof HTMLElement) || section.dataset.cpsbrgReady === 'true') return;

  const dialog = section.querySelector('[data-cpsbrg-dialog]');
  if (!(dialog instanceof HTMLDialogElement)) return;

  section.dataset.cpsbrgReady = 'true';
  initRoomCardInteractions(section);

  section.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-cpsbrg-open]');
    const option = target.closest('[data-cpsbrg-option]');
    const thumb = target.closest('[data-cpsbrg-gallery-thumb]');
    const galleryButton = target.closest('[data-cpsbrg-gallery-nav]');
    const zoomButton = target.closest('[data-cpsbrg-zoom]');
    const close = target.closest('[data-cpsbrg-close]');

    if (opener instanceof HTMLElement) {
      event.preventDefault();
      openPanel(section, opener.dataset.cpsbrgOpen || '');
      return;
    }

    if (option instanceof HTMLButtonElement) {
      selectOption(option);
      return;
    }

    if (thumb instanceof HTMLButtonElement) {
      event.preventDefault();
      showGallerySlide(thumb.closest('[data-cpsbrg-panel]'), Number(thumb.dataset.cpsbrgGalleryIndex || 0));
      return;
    }

    if (galleryButton instanceof HTMLElement) {
      event.preventDefault();
      stepGallery(galleryButton);
      return;
    }

    if (zoomButton instanceof HTMLElement) {
      event.preventDefault();
      openGalleryZoom(zoomButton);
      return;
    }

    if (close) {
      closeDialog(section);
    }
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      closeDialog(section);
    }
  });

  dialog.addEventListener('close', () => {
    getPanels(section).forEach((panel) => {
      panel.hidden = true;
    });
    restoreFocus(section);
  });

  dialog.addEventListener('cart:update', (event) => {
    if (!event.detail?.data?.didError) {
      closeDialog(section);
    }
  });
}

function init(root = document) {
  if (root instanceof HTMLElement && root.matches(sectionSelector)) {
    initSection(root);
  }

  root.querySelectorAll(sectionSelector).forEach(initSection);
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-cpsbrg-open]');
    if (!(opener instanceof HTMLElement)) return;

    const section = opener.closest(sectionSelector);
    if (section instanceof HTMLElement && section.dataset.cpsbrgReady !== 'true') {
      initSection(section);
    }
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});

document.addEventListener('shop-by-room:open-quick-view', (event) => {
  const request = event.detail || {};

  if (!request.panelId && !request.productHandle && !request.title) return;

  const section = Array.from(document.querySelectorAll(sectionSelector)).find((item) =>
    Boolean(findPanel(item, request))
  );

  if (section instanceof HTMLElement) {
    initSection(section);
    openPanel(section, request);
  }
});
